import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { rotaApi, rotaKeys, type RequirementInput } from "./api";

const REFERENCE = 5 * 60 * 1000;

export function useShifts() {
  return useQuery({ queryKey: rotaKeys.shifts(), queryFn: rotaApi.shifts, staleTime: REFERENCE });
}

export function usePositions(includeInactive = false) {
  return useQuery({
    queryKey: rotaKeys.positions(includeInactive),
    queryFn: () => rotaApi.positions(includeInactive),
    staleTime: REFERENCE,
  });
}

export function useRequirements(branchId?: string) {
  return useQuery({
    queryKey: rotaKeys.requirements(branchId),
    queryFn: () => rotaApi.requirements(branchId),
    staleTime: REFERENCE,
  });
}

export function useRotaWeek(from: string, to: string, branchId?: string) {
  return useQuery({
    queryKey: rotaKeys.week(from, to, branchId),
    queryFn: () => rotaApi.week(from, to, branchId),
  });
}

export function useMyRota(from: string, to: string) {
  return useQuery({
    queryKey: rotaKeys.mine(from, to),
    queryFn: () => rotaApi.mine(from, to),
  });
}

function useRotaMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rotaKeys.all });
    },
  });
}

export function useAssignShift() {
  return useRotaMutation(rotaApi.assign);
}

export function useMoveShift() {
  return useRotaMutation(
    (args: { assignmentId: string; changes: Parameters<typeof rotaApi.move>[1] }) =>
      rotaApi.move(args.assignmentId, args.changes),
  );
}

export function useUnassignShift() {
  return useRotaMutation(rotaApi.unassign);
}

export function useDrawRota() {
  return useRotaMutation(rotaApi.draw);
}

export function useGuidelineRequirements(branchId: string) {
  return useRotaMutation(() => rotaApi.guidelineRequirements(branchId));
}

export function useSetRequirements(branchId: string) {
  return useRotaMutation((requirements: RequirementInput[]) =>
    rotaApi.setRequirements(branchId, requirements),
  );
}

export function useCreatePosition() {
  return useRotaMutation(rotaApi.createPosition);
}

export function useUpdatePosition() {
  return useRotaMutation((args: { id: string; changes: Parameters<typeof rotaApi.updatePosition>[1] }) =>
    rotaApi.updatePosition(args.id, args.changes),
  );
}

export function useDeletePosition() {
  return useRotaMutation(rotaApi.deletePosition);
}

export function useSendJobDescription() {
  return useRotaMutation(rotaApi.sendJobDescription);
}

export function useUpdateShift() {
  return useRotaMutation((args: { id: string; changes: Parameters<typeof rotaApi.updateShift>[1] }) =>
    rotaApi.updateShift(args.id, args.changes),
  );
}

/** Where each branch will be short, and by how much, week by week. */
export function useStaffingForecast(weeks = 9) {
  return useQuery({
    queryKey: rotaKeys.forecast(weeks),
    queryFn: () => rotaApi.forecast(weeks),
    staleTime: 5 * 60 * 1000,
  });
}
